import StyledTooltip from "@/components/styled-tooltip";
import { Card } from "@/components/ui/card";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Separator } from "@/components/ui/separator";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { DnsRecordsResponse } from "@/types/postman.types";
import { ChevronDown, Info, XCircle } from "lucide-react";
import { useMemo } from "react";

type Props = {
  healthcheck: DnsRecordsResponse["healthcheck"];
};

const EmailHealthProblems = ({ healthcheck }: Props) => {
  const problems = useMemo(() => {
    return healthcheck?.checkResults
      ?.filter((item) => item.status === "warning" || item.status === "fail")
      .sort((a, b) => {
        return a.status === "fail" ? -1 : b.status === "fail" ? 1 : 0;
      });
  }, [healthcheck?.checkResults]);

  return (
    <Card className="mt-5 mb-4">
      <Collapsible defaultOpen>
        <CollapsibleTrigger className="flex items-center justify-between w-full px-4 py-3 [&[data-state=open]>svg]:rotate-180">
          <div className="  flex items-center justify-between">
            <h3 className="font-medium flex items-center gap-2">
              <XCircle className="w-4 h-4 text-red-500" />
              {problems?.length || 0}{" "}
              <span className="flex items-center gap-1">
                Problems
                <StyledTooltip description="The DNS errors are the errors that were found in the DNS records of your domain.">
                  <Info className="w-3 h-3 text-muted-foreground" />
                </StyledTooltip>
              </span>
            </h3>
          </div>
          <ChevronDown className="w-4 h-4 transition-transform duration-200" />
        </CollapsibleTrigger>
        <CollapsibleContent>
          <Separator />
          <div className="">
            <div className="rounded-b-lg overflow-hidden">
              <Table className="w-full text-sm">
                <TableHeader>
                  <TableRow className="border-b bg-muted/50">
                    <TableHead className="py-2 px-4 text-left font-medium hidden md:table-cell">
                      Category
                    </TableHead>
                    <TableHead className="py-2 px-4 text-left font-medium hidden md:table-cell">
                      Name
                    </TableHead>
                    <TableHead className="py-2 px-4 text-left font-medium hidden md:table-cell">
                      Message
                    </TableHead>
                    <TableHead className="py-2 px-4 text-left font-medium hidden md:table-cell">
                      Status
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {problems?.map((error, index) => (
                    <TableRow
                      key={index}
                      className={`border-b last:border-0 ${
                        index % 2 === 0 ? "bg-secondary" : "bg-muted/50"
                      }`}
                    >
                      <TableCell className="py-3 px-4">
                        <div className="items-center gap-2 hidden md:flex">
                          <div
                            className={`h-2.5 w-2.5 rounded-full ${
                              error.status === "warning"
                                ? "bg-yellow-500"
                                : "bg-red-500"
                            }`}
                          />
                          <span className="font-medium">{error.category}</span>
                        </div>

                        <div className="md:hidden space-y-1.5">
                          <div className="flex items-center gap-2">
                            <span className=" text-muted-foreground min-w-[80px]">
                              Category:
                            </span>
                            <div className="flex items-center gap-1">
                              <div
                                className={`h-2.5 w-2.5 rounded-full shrink-0 ${
                                  error.status === "warning"
                                    ? "bg-yellow-500"
                                    : "bg-red-500"
                                }`}
                              />
                              {error.category}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className=" text-muted-foreground min-w-[80px]">
                              Name:
                            </span>
                            {error.name}
                          </div>

                          <div className="flex items-center gap-2">
                            <span className=" text-muted-foreground min-w-[80px]">
                              Status:
                            </span>
                            {error.status}
                          </div>
                          <div className="flex gap-2">
                            <span className=" text-muted-foreground min-w-[80px]">
                              Message:
                            </span>
                            {error.message || "--"}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="py-3 px-4 hidden md:table-cell">
                        {error.name}
                      </TableCell>
                      <TableCell className="py-3 px-4 hidden md:table-cell">
                        {error.message || "--"}
                      </TableCell>
                      <TableCell className="py-3 px-4 hidden md:table-cell">
                        <div className="flex ">
                          <span
                            className={`px-2 py-1 rounded-md text-xs font-medium uppercase ${
                              error.status === "warning"
                                ? "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400"
                                : "bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400"
                            }`}
                          >
                            {error.status}
                          </span>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
};

export default EmailHealthProblems;
